import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { TicketUpdated } from '@/types/ticket';
import { ticketService } from '@/api/services/ticketService';
import type { RootState } from '@/store/store';

interface TicketsState {
    activeTicket: string | null;
    byId: Record<string, TicketUpdated>;
    allIds: string[];
}

const initialState: TicketsState = {
    activeTicket: null,
    byId: {},
    allIds: [],
};

export const fetchTicketsByPage = createAsyncThunk('tickets/fetchTicketsByPage', async (params: { page: number, view: "all" | "unassigned" | "my-inbox" }, { rejectWithValue }) => {
    try {
        console.log("fetching tickets by page", params);
        const response = await ticketService.getTicketsByPage({ page: params.page, limit: 20, sortOrder: "desc", view: params.view });
        return response;
    } catch (error) {
        return rejectWithValue(error);
    }
});

// Create a thunk that checks if the message received is for the active ticket if yes then send a read receipt to server
// Function will accept a callback and if active ticket matches it will call the callback with the message
export const checkIfMessageIsForActiveTicket = createAsyncThunk(
    'tickets/checkIfMessageIsForActiveTicket',
    (params: { ticketId: string, callback: () => void }, { getState }) => {
        const state = getState() as RootState;
        const { ticketId, callback } = params;

        if (state.ticket.activeTicket === ticketId) {
            callback();
        }
    }
);

const ticketsSlice = createSlice({
    name: 'ticket',
    initialState,
    reducers: {
        setActiveTicket: (state, action: PayloadAction<string>) => {
            state.activeTicket = action.payload;
        },
        upsertTickets: (state, action: PayloadAction<TicketUpdated[]>) => {
            action.payload.forEach(ticket => {
                state.byId[ticket.id] = ticket;
                if (!state.allIds.includes(ticket.id)) {
                    state.allIds.push(ticket.id);
                }
            });
        },
        updateTicket: (state, action: PayloadAction<TicketUpdated>) => {
            state.byId[action.payload.id] = action.payload;
            if (!state.allIds.includes(action.payload.id)) {
                state.allIds.push(action.payload.id);
            }
        },
        removeTicket: (state, action: PayloadAction<string>) => {
            delete state.byId[action.payload];
            state.allIds = state.allIds.filter(id => id !== action.payload);
        },
        clearTickets: (state) => {
            state.byId = {};
            state.allIds = [];
        },
        addNewTicket: (state, action: PayloadAction<TicketUpdated>) => {
            const ticket = action.payload;

            // Add ticket to normalized store
            state.byId[ticket.id] = ticket;

            // Add to beginning of allIds if not already present
            if (!state.allIds.includes(ticket.id)) {
                state.allIds.unshift(ticket.id);
            }
        },
        updateTicketField: (state, action: PayloadAction<{ ticketId: string, field: string, value: string }>) => {
            const { ticketId, field, value } = action.payload;
            const ticket = state.byId[ticketId];
            if (ticket) {
                ticket[field] = value;
                state.byId[ticketId] = ticket;
            }
        },
        updateTicketDataSync: (state, action) => {
            const { id } = action.payload;
            // Check if the customer is in the state if yes then update otherwise ignore
            if (state.byId[id]) {
                // Loop over every key in the action.payload and update the customerMappedById
                Object.keys(action.payload).forEach(key => {
                    if (key === 'id') {
                        return;
                    }
                    state.byId[id][key] = action.payload[key];
                });
            }
        }
    },
    extraReducers: (builder) => {
        builder.addCase(fetchTicketsByPage.fulfilled, (state, action) => {
            action.payload.tickets.forEach(ticket => {
                state.byId[ticket.id] = ticket;
                if (!state.allIds.includes(ticket.id)) {
                    state.allIds.push(ticket.id);
                }
            });
        });
    },
});

// Selectors
export const selectTicketsById = (state: RootState) => state.ticket.byId;
export const selectAllTicketIds = (state: RootState) => state.ticket.allIds;
export const selectTickets = (state: RootState) =>
    state.ticket.allIds.map(id => state.ticket.byId[id]);
export const selectTicketById = (id: string) => (state: RootState) =>
    state.ticket.byId[id];

export const createSelectTicketById = (id: string | null) => (state: RootState) =>
    id ? state.ticket.byId[id] : null;

export const selectActiveTicket = (state: RootState) => state.ticket.activeTicket;

export const { upsertTickets, updateTicket, removeTicket, clearTickets, addNewTicket, updateTicketField, setActiveTicket, updateTicketDataSync } = ticketsSlice.actions;
export const ticketsReducer = ticketsSlice.reducer; 